import React from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Checkbox,
  Code,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  Icon,
  Input,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  OrderedList,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Textarea,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { InfoOutlineIcon, RepeatIcon, StarIcon } from "@chakra-ui/icons";
import { useGlobalAction, useUser } from "@gadgetinc/react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api";
import { hasAnyRole, isInstitutionAdministrator } from "../lib/roleUtils";

const TUTORIAL_STORAGE_PREFIX = "liveQaTutorial:v1:";

const defaultWorkflowDraft = {
  name: "Live QA Default Workflow",
  institution: "",
  intakeConfig: {
    emailKeyword: "live",
    enabledSourceTypes: ["email", "upload", "structured", "text"],
  },
  clinicalPolicyConfig: {
    enableSharedModelPracticeFallback: true,
    requiredNarratives: ["pcr", "medicalNecessity"],
  },
  billingPolicyConfig: {
    enableUniversalCmsChecks: true,
    customRules: [],
  },
  compliancePolicyConfig: {
    controlledSubstances: [],
    requirePatientOrRepSignature: false,
    signatureRequirementMode: "flag",
    witnessRequirementMode: "flag",
  },
  knowledgeBaseConfig: {
    enabled: false,
    openaiVectorStoreId: "",
    retrieval: {
      maxClinicalSnippets: 4,
      maxBillingSnippets: 3,
      maxComplianceSnippets: 3,
    },
  },
  requiredMappingRules: [],
  notificationConfig: {
    staticRecipients: [],
    returnToAuthor: true,
    notifyOn: ["completed", "needs_review"],
  },
  reportStyleConfig: {
    includeReviewLenses: true,
    includePerPatientFeedback: true,
    includePhysicianFollowupQuestions: true,
  },
  reviewerEmails: [],
  operatorEmails: [],
};

const defaultGroupDraft = {
  id: "",
  name: "",
  slug: "",
  active: true,
  matchConfig: {
    matchAny: [],
  },
  routingConfig: {
    verdictRecipients: {},
  },
};

const helpContent = {
  intakeConfig: {
    title: "Intake Config",
    description: "Controls how Live QA accepts charts into this workflow.",
    when: "Change this when you need a different mailbox keyword or want to restrict source types.",
    example: `{
  "emailKeyword": "live",
  "enabledSourceTypes": ["email", "upload", "structured", "text"]
}`,
    mistakes: "Do not reuse FISDAP-style subject keywords here. Keep the source list aligned with what your agency will actually send.",
  },
  knowledgeBaseConfig: {
    title: "Knowledge Base",
    description: "Connects the workflow to the agency-owned KB and retrieval settings.",
    when: "Change this after you create the KB or if you need different retrieval limits by domain.",
    example: `{
  "enabled": true,
  "openaiVectorStoreId": "vs_...",
  "retrieval": { "maxClinicalSnippets": 4, "maxBillingSnippets": 3, "maxComplianceSnippets": 3 }
}`,
    mistakes: "Do not type a vector-store ID manually unless you know it is correct. Prefer creating the KB from the Knowledge Base tab.",
  },
  clinicalPolicyConfig: {
    title: "Clinical Policy",
    description: "Sets shared fallback behavior and baseline documentation expectations for clinical review.",
    when: "Change this when an agency has strong preferences around required narratives or fallback guidance behavior.",
    example: `{
  "enableSharedModelPracticeFallback": true,
  "requiredNarratives": ["pcr", "medicalNecessity"]
}`,
    mistakes: "Do not disable fallback unless the agency KB already covers the clinical areas you expect to evaluate.",
  },
  billingPolicyConfig: {
    title: "Billing Policy",
    description: "Adds universal CMS checks and agency-specific billing rules.",
    when: "Change this when the agency has custom transport or signature requirements beyond the universal CMS pack.",
    example: `{
  "enableUniversalCmsChecks": true,
  "customRules": [
    { "name": "IFT medical necessity", "description": "Flag when interfacility transport lacks supporting medical necessity language." }
  ]
}`,
    mistakes: "Keep custom rules concrete and auditable. Avoid vague phrases that cannot be traced to chart evidence.",
  },
  compliancePolicyConfig: {
    title: "Compliance Policy",
    description: "Controls signature expectations, witness handling, and medication-specific compliance checks.",
    when: "Change this when the agency has narcotic handling or signature requirements.",
    example: `{
  "controlledSubstances": ["fentanyl", "midazolam"],
  "requirePatientOrRepSignature": true,
  "signatureRequirementMode": "flag",
  "witnessRequirementMode": "flag"
}`,
    mistakes: "Only include meds the agency actually uses. Overly broad lists will create noisy findings.",
  },
  requiredMappingRules: {
    title: "Required Mapping Rules",
    description: "Fields that should be enforced as present, noted, flagged, or review-blocking.",
    when: "Use this when a field is critical for local QA, billing, or compliance, even if not every chart maps every NEMSIS field.",
    example: `[
  {
    "canonicalPath": "narrative.medicalNecessity",
    "nemsisElementId": "eNarrative.01",
    "sourceTypes": ["api", "pdf", "text"],
    "allowContextualInference": true,
    "failureAction": "flag",
    "dependentDomains": ["billing"]
  }
]`,
    mistakes: "Do not mark a field as requires_review unless missing it truly prevents a defensible evaluation.",
  },
  notificationConfig: {
    title: "Notification Config",
    description: "Controls who gets reports and when.",
    when: "Change this when the agency wants fixed recipients, author copies, or narrower verdict triggers.",
    example: `{
  "staticRecipients": ["qa@example.org"],
  "returnToAuthor": true,
  "notifyOn": ["completed", "needs_review"]
}`,
    mistakes: "Separate reviewer/operator assignment from notification recipients. They solve different problems.",
  },
  reportStyleConfig: {
    title: "Report Style",
    description: "Controls how the final synthesized report is rendered.",
    when: "Change this only if an agency wants a leaner report format.",
    example: `{
  "includeReviewLenses": true,
  "includePerPatientFeedback": true,
  "includePhysicianFollowupQuestions": true
}`,
    mistakes: "Turning off per-patient or follow-up sections makes the output less aligned with the FISDAP reader style.",
  },
};

const tutorialSteps = [
  { title: "Live QA Purpose", body: "This configurator is separate from FISDAP. It governs live-chart intake, QA policy, KB grounding, test runs, and chart review for the standalone Live QA pipeline.", target: "overview", tabIndex: 0 },
  { title: "Workflow Selection", body: "Choose the workflow and institution first. All policy, groups, and knowledge-base state on this screen are scoped to the selected workflow.", target: "workflow-header", tabIndex: 1 },
  { title: "Intake", body: "Use Intake Config to control the email keyword and allowed source types. This is where you keep live-chart intake isolated from FISDAP subjects.", target: "workflow-intake", tabIndex: 1 },
  { title: "Knowledge Base", body: "Use the Knowledge Base tab to create the agency KB, upload protocols and policies, and confirm sync state. The workflow config should point to the created vector store.", target: "kb-manager", tabIndex: 3 },
  { title: "Clinical Policy", body: "Clinical Policy controls narrative expectations and whether the workflow can fall back to the shared model-practice guidance when agency documents do not cover a topic.", target: "workflow-clinical", tabIndex: 1 },
  { title: "Billing Policy", body: "Billing Policy turns on universal CMS checks and holds agency-specific billing rules.", target: "workflow-billing", tabIndex: 1 },
  { title: "Compliance Policy", body: "Compliance Policy is where you encode narcotic, witness, and signature expectations.", target: "workflow-compliance", tabIndex: 1 },
  { title: "Required Mappings", body: "Required Mapping Rules let the agency enforce specific canonical or NEMSIS-aligned fields. Use note_only, flag, or requires_review based on how critical the field really is.", target: "workflow-mappings", tabIndex: 1 },
  { title: "Groups", body: "Groups let you classify charts for routing and tailored review. Build them with name, matchConfig, and routingConfig.", target: "groups-manager", tabIndex: 2 },
  { title: "Reviewers and Notifications", body: "Reviewer/operator assignments control who can work the queue. Notification Config controls who gets report emails and when.", target: "workflow-notifications", tabIndex: 1 },
  { title: "Test Run", body: "Use Test Run to upload a sample PDF, paste text, or submit a structured payload and see the de-identified, mapped output immediately.", target: "test-run", tabIndex: 4 },
  { title: "Charts", body: "Charts shows processed records, extraction results, synthesized output, and field-level mapping audit for review.", target: "charts-detail", tabIndex: 5 },
];

const tabIndexBySection = tutorialSteps.reduce((acc, step) => {
  acc[step.target] = step.tabIndex;
  return acc;
}, {});

const assistantExamples = [
  "Require patient or authorized representative signature and flag missing signatures.",
  "Add qa@agency.org as a reviewer and notify billing@example.org on completed and needs_review reports.",
  "Create a Chest Pain group and leave the routing rules blank for later refinement.",
  "Require medical necessity to be mapped for pdf, text, and api charts and flag it when missing.",
];

const groupPresets = [
  {
    name: "ALS",
    slug: "als",
    matchConfig: { transportType: ["ALS"], matchAny: ["als", "cardiac monitor", "iv"] },
    routingConfig: { verdictRecipients: { fail: ["qa@agency.org"] } },
  },
  {
    name: "Stroke",
    slug: "stroke",
    matchConfig: { matchAny: ["stroke", "cva", "lvo", "be-fast"] },
    routingConfig: { verdictRecipients: { fail: ["strokeqa@agency.org"] } },
  },
  {
    name: "Controlled Substance",
    slug: "controlled-substance",
    matchConfig: { matchAny: ["fentanyl", "midazolam", "morphine", "ketamine", "waste", "witness"] },
    routingConfig: { verdictRecipients: { fail: ["pharmacyqa@agency.org"] } },
  },
];

const quickSetupGroupOptions = [
  { key: "als", label: "ALS" },
  { key: "chestPain", label: "Chest Pain" },
  { key: "stroke", label: "Stroke" },
  { key: "controlledSubstance", label: "Controlled Substance" },
];

function canAccess(user) {
  if (!user) return false;
  return hasAnyRole(user?.roles || user?.roleList || [], ["admin", "system-admin", "sysadmin"]) || isInstitutionAdministrator(user);
}

function buildSlug(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function parseEmailList(value) {
  return Array.from(
    new Set(
      String(value || "")
        .split(/[\n,;]+/g)
        .map((entry) => entry.trim().toLowerCase())
        .filter(Boolean)
    )
  );
}

function joinEmailList(values) {
  return Array.from(new Set((values || []).map((entry) => String(entry || "").trim()).filter(Boolean))).join(", ");
}

function HelpCard({ sectionKey, accent = "blue.50" }) {
  const content = helpContent[sectionKey];
  if (!content) return null;

  return (
    <Box borderWidth="1px" borderRadius="lg" p={4} bg={accent}>
      <Stack spacing={3}>
        <HStack spacing={2}>
          <Icon as={InfoOutlineIcon} color="blue.500" />
          <Heading size="sm">{content.title}</Heading>
        </HStack>
        <Text fontSize="sm">{content.description}</Text>
        <Text fontSize="sm" color="gray.700"><strong>When to change it:</strong> {content.when}</Text>
        <Text fontSize="sm" color="gray.700"><strong>Common mistake:</strong> {content.mistakes}</Text>
        <Code whiteSpace="pre-wrap" p={3} fontSize="xs">{content.example}</Code>
      </Stack>
    </Box>
  );
}

function JsonEditorCard({ label, sectionKey, value, onChange, onReset, minHeight = "220px", sectionRef, sectionId }) {
  return (
    <Box borderWidth="1px" borderRadius="lg" p={4} ref={sectionRef} data-section={sectionId}>
      <Stack spacing={4}>
        <Flex justify="space-between" align="center" gap={3}>
          <Heading size="sm">{label}</Heading>
          <Button size="sm" variant="ghost" leftIcon={<RepeatIcon />} onClick={onReset}>Reset to Default</Button>
        </Flex>
        <HelpCard sectionKey={sectionKey} />
        <Textarea
          value={value}
          onChange={(event) => onChange(event.target.value)}
          minH={minHeight}
          fontFamily="mono"
          fontSize="sm"
          bg="gray.900"
          color="gray.100"
        />
      </Stack>
    </Box>
  );
}

function applyPreviewToGroups(currentGroups, workflowId, previewGroups) {
  const otherGroups = (currentGroups || []).filter((entry) => entry?.workflow?.id !== workflowId);
  const nextGroups = (previewGroups || []).map((entry) => ({
    ...entry,
    workflow: entry.workflow || { id: workflowId },
  }));
  return [...otherGroups, ...nextGroups];
}

export default function AdminLiveQa() {
  const isClient = typeof window !== "undefined";
  const user = useUser(api);
  const [, sendInvite] = useGlobalAction(api.sendInvite);
  const navigate = useNavigate();
  const tutorial = useDisclosure();
  const reference = useDisclosure();
  const assistant = useDisclosure();
  const quickSetup = useDisclosure();
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [running, setRunning] = React.useState(false);
  const [quickSetupBusy, setQuickSetupBusy] = React.useState(false);
  const [groupSaving, setGroupSaving] = React.useState(false);
  const [kbBusy, setKbBusy] = React.useState(false);
  const [assistantBusy, setAssistantBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");
  const [workflows, setWorkflows] = React.useState([]);
  const [selectedWorkflowId, setSelectedWorkflowId] = React.useState("");
  const [workflowDraft, setWorkflowDraft] = React.useState(defaultWorkflowDraft);
  const [charts, setCharts] = React.useState([]);
  const [selectedChart, setSelectedChart] = React.useState(null);
  const [groups, setGroups] = React.useState([]);
  const [groupDraft, setGroupDraft] = React.useState(defaultGroupDraft);
  const [knowledgeBases, setKnowledgeBases] = React.useState([]);
  const [knowledgeDocuments, setKnowledgeDocuments] = React.useState([]);
  const [newKbName, setNewKbName] = React.useState("");
  const [kbUploadFile, setKbUploadFile] = React.useState(null);
  const [kbUploadTitle, setKbUploadTitle] = React.useState("");
  const [kbUploadTags, setKbUploadTags] = React.useState("clinical_protocol");
  const [tabIndex, setTabIndex] = React.useState(0);
  const [tutorialStep, setTutorialStep] = React.useState(0);
  const [testMode, setTestMode] = React.useState("artifact");
  const [testFile, setTestFile] = React.useState(null);
  const [testText, setTestText] = React.useState("");
  const [testPayload, setTestPayload] = React.useState("{\n  \"chiefComplaint\": \"Chest pain\"\n}");
  const [assistantInput, setAssistantInput] = React.useState("");
  const [assistantScope, setAssistantScope] = React.useState(["workflow"]);
  const [assistantPreview, setAssistantPreview] = React.useState(null);
  const [quickSetupDraft, setQuickSetupDraft] = React.useState({
    institution: "",
    workflowName: "",
    reviewerEmails: "",
    operatorEmails: "",
    createDemoGroups: true,
    groupPresetKeys: quickSetupGroupOptions.map((entry) => entry.key),
    sendInvitesToListedUsers: false,
  });
  const sectionRefs = React.useRef({});

  const storageKey = React.useMemo(() => `${TUTORIAL_STORAGE_PREFIX}${user?.id || "anonymous"}`, [user?.id]);
  const workflowGroups = React.useMemo(
    () => (groups || []).filter((entry) => entry?.workflow?.id === selectedWorkflowId),
    [groups, selectedWorkflowId]
  );
  const workflowKnowledgeBase = React.useMemo(
    () => (knowledgeBases || []).find((entry) => entry?.workflow?.id === selectedWorkflowId) || null,
    [knowledgeBases, selectedWorkflowId]
  );
  const workflowKnowledgeDocuments = React.useMemo(
    () => (knowledgeDocuments || []).filter((entry) => entry?.knowledgeBase?.workflow?.id === selectedWorkflowId),
    [knowledgeDocuments, selectedWorkflowId]
  );

  const buildQuickSetupDraft = React.useCallback(() => {
    const fallbackInstitution = String(workflowDraft.institution || user?.institution || "").trim();
    const reviewerSeed = parseEmailList(joinEmailList([
      ...(Array.isArray(workflowDraft.reviewerEmails) ? workflowDraft.reviewerEmails : []),
      user?.email || "",
    ]));
    const operatorSeed = parseEmailList(joinEmailList([
      ...(Array.isArray(workflowDraft.operatorEmails) ? workflowDraft.operatorEmails : []),
      user?.email || "",
    ]));

    return {
      institution: fallbackInstitution,
      workflowName: String(
        workflowDraft.name ||
          (fallbackInstitution ? `${fallbackInstitution} Live QA Pilot` : "Live QA Pilot")
      ).trim(),
      reviewerEmails: joinEmailList(reviewerSeed),
      operatorEmails: joinEmailList(operatorSeed),
      createDemoGroups: true,
      groupPresetKeys: quickSetupGroupOptions.map((entry) => entry.key),
      sendInvitesToListedUsers: false,
    };
  }, [workflowDraft, user?.email, user?.institution]);

  const registerSectionRef = React.useCallback((sectionId) => (node) => {
    sectionRefs.current[sectionId] = node;
  }, []);

  const loadConfig = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.fetch("/live-qa/config");
      const payload = await response.json();
      if (payload?.success === false) {
        throw new Error(payload.error || "Failed to load live QA configuration");
      }

      const nextWorkflows = payload?.workflows || [];
      const nextWorkflowId = selectedWorkflowId && nextWorkflows.some((entry) => entry.id === selectedWorkflowId)
        ? selectedWorkflowId
        : nextWorkflows[0]?.id || "";
      const nextWorkflow = nextWorkflows.find((entry) => entry.id === nextWorkflowId) || defaultWorkflowDraft;

      setWorkflows(nextWorkflows);
      setGroups(payload?.groups || []);
      setKnowledgeBases(payload?.knowledgeBases || []);
      setKnowledgeDocuments(payload?.knowledgeDocuments || []);
      setSelectedWorkflowId(nextWorkflowId);
      setWorkflowDraft(nextWorkflow);
    } catch (loadError) {
      setError(loadError.message || "Failed to load live QA configuration");
    } finally {
      setLoading(false);
    }
  }, [selectedWorkflowId]);

  const loadCharts = React.useCallback(async (workflowId) => {
    if (!workflowId) return;
    try {
      const response = await api.fetch(`/live-qa/charts?workflowId=${encodeURIComponent(workflowId)}`);
      const payload = await response.json();
      if (payload?.success === false) {
        throw new Error(payload.error || "Failed to load charts");
      }
      setCharts(payload?.charts || []);
    } catch (loadError) {
      setError(loadError.message || "Failed to load charts");
    }
  }, []);

  const loadChartDetail = React.useCallback(async (chartId) => {
    try {
      const response = await api.fetch(`/live-qa/charts/${chartId}`);
      const payload = await response.json();
      if (payload?.success === false) {
        throw new Error(payload.error || "Failed to load chart detail");
      }
      setSelectedChart(payload);
    } catch (loadError) {
      setError(loadError.message || "Failed to load chart detail");
    }
  }, []);

  React.useEffect(() => {
    if (user && !canAccess(user)) {
      navigate("/admin");
      return;
    }
    if (user) {
      loadConfig();
      const savedStep = Number(window.localStorage.getItem(storageKey) || 0);
      if (!Number.isNaN(savedStep)) {
        setTutorialStep(Math.max(0, Math.min(tutorialSteps.length - 1, savedStep)));
      }
    }
  }, [user, navigate, loadConfig, storageKey]);

  React.useEffect(() => {
    if (selectedWorkflowId) {
      loadCharts(selectedWorkflowId);
    }
  }, [selectedWorkflowId, loadCharts]);

  React.useEffect(() => {
    if (!tutorial.isOpen) return;
    const step = tutorialSteps[tutorialStep];
    const nextTabIndex = tabIndexBySection[step?.target] ?? 0;
    setTabIndex(nextTabIndex);
    window.localStorage.setItem(storageKey, String(tutorialStep));
    window.setTimeout(() => {
      const node = sectionRefs.current[step?.target];
      if (node?.scrollIntoView) {
        node.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 150);
  }, [tutorial.isOpen, tutorialStep, storageKey]);

  React.useEffect(() => {
    if (quickSetup.isOpen) {
      setQuickSetupDraft(buildQuickSetupDraft());
    }
  }, [quickSetup.isOpen, buildQuickSetupDraft]);

  const updateJsonField = React.useCallback((field, value, fallback) => {
    try {
      setWorkflowDraft((current) => ({ ...current, [field]: JSON.parse(value || fallback) }));
      setError("");
    } catch (_parseError) {
      setError(`${field} is not valid JSON yet`);
    }
  }, []);

  const handleWorkflowSelection = React.useCallback((workflowId) => {
    setSelectedWorkflowId(workflowId);
    const workflow = workflows.find((entry) => entry.id === workflowId);
    setWorkflowDraft(workflow || defaultWorkflowDraft);
    setSelectedChart(null);
    setAssistantPreview(null);
    setGroupDraft(defaultGroupDraft);
  }, [workflows]);

  const handleSaveWorkflow = async () => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const payload = {
        ...workflowDraft,
        id: selectedWorkflowId || workflowDraft.id,
        requiredMappingRules: Array.isArray(workflowDraft.requiredMappingRules)
          ? workflowDraft.requiredMappingRules
          : JSON.parse(workflowDraft.requiredMappingRules || "[]"),
        reviewerEmails: Array.isArray(workflowDraft.reviewerEmails)
          ? workflowDraft.reviewerEmails
          : String(workflowDraft.reviewerEmails || "").split(",").map((entry) => entry.trim()).filter(Boolean),
        operatorEmails: Array.isArray(workflowDraft.operatorEmails)
          ? workflowDraft.operatorEmails
          : String(workflowDraft.operatorEmails || "").split(",").map((entry) => entry.trim()).filter(Boolean),
      };

      const response = await api.fetch("/live-qa/config", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (!response.ok || result?.success === false) {
        throw new Error(result?.error || "Failed to save workflow");
      }
      setSuccess("Workflow saved");
      await loadConfig();
    } catch (saveError) {
      setError(saveError.message || "Failed to save workflow");
    } finally {
      setSaving(false);
    }
  };

  const readFileAsBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || "").split(",")[1] || "");
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleRunTest = async () => {
    if (!selectedWorkflowId) {
      setError(workflows.length === 0 ? "Create or save a Live QA workflow before running a test chart." : "Select a Live QA workflow before running the pipeline.");
      setSuccess("");
      setTabIndex(1);
      return;
    }
    setRunning(true);
    setError("");
    setSuccess("");
    try {
      const body = {
        workflowId: selectedWorkflowId,
        sourceType: testMode === "structured" ? "structured" : testMode === "text" ? "manual_text" : "artifact",
      };

      if (testMode === "artifact") {
        if (!testFile) throw new Error("Choose a file first");
        body.base64 = await readFileAsBase64(testFile);
        body.fileName = testFile.name;
        body.mimeType = testFile.type || "application/pdf";
      } else if (testMode === "text") {
        body.text = testText;
      } else {
        body.payload = JSON.parse(testPayload || "{}");
      }

      const response = await api.fetch("/live-qa/run-test", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const result = await response.json();
      if (!response.ok || result?.success === false) throw new Error(result?.error || "Failed to run test");
      setSuccess("Test chart processed");
      await loadCharts(selectedWorkflowId);
      if (result?.chartId) await loadChartDetail(result.chartId);
      setTabIndex(5);
    } catch (runError) {
      setError(runError.message || "Failed to run test");
    } finally {
      setRunning(false);
    }
  };

  const saveGroup = async () => {
    if (!selectedWorkflowId) return;
    setGroupSaving(true);
    setError("");
    setSuccess("");
    try {
      const response = await api.fetch("/live-qa/groups", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...groupDraft,
          workflowId: selectedWorkflowId,
          slug: groupDraft.slug || buildSlug(groupDraft.name),
        }),
      });
      const result = await response.json();
      if (!response.ok || result?.success === false) {
        throw new Error(result?.error || "Failed to save group");
      }
      setSuccess("Group saved");
      setGroupDraft(defaultGroupDraft);
      await loadConfig();
    } catch (groupError) {
      setError(groupError.message || "Failed to save group");
    } finally {
      setGroupSaving(false);
    }
  };

  const archiveGroup = async (groupId) => {
    setGroupSaving(true);
    setError("");
    try {
      const response = await api.fetch(`/live-qa/groups/${groupId}`, {
        method: "DELETE",
      });
      const result = await response.json();
      if (!response.ok || result?.success === false) {
        throw new Error(result?.error || "Failed to archive group");
      }
      setSuccess("Group archived");
      if (groupDraft.id === groupId) setGroupDraft(defaultGroupDraft);
      await loadConfig();
    } catch (groupError) {
      setError(groupError.message || "Failed to archive group");
    } finally {
      setGroupSaving(false);
    }
  };

  const applyGroupPreset = (preset) => {
    setGroupDraft({
      ...defaultGroupDraft,
      ...preset,
    });
  };

  const createKnowledgeBase = async () => {
    if (!selectedWorkflowId) return;
    setKbBusy(true);
    setError("");
    try {
      const response = await api.fetch("/live-qa/knowledge-base", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          workflowId: selectedWorkflowId,
          name: newKbName || `${workflowDraft.institution || "Agency"} Live QA KB`,
        }),
      });
      const result = await response.json();
      if (!response.ok || result?.success === false) {
        throw new Error(result?.error || "Failed to create knowledge base");
      }
      setSuccess("Knowledge base created");
      setNewKbName("");
      await loadConfig();
    } catch (kbError) {
      setError(kbError.message || "Failed to create knowledge base");
    } finally {
      setKbBusy(false);
    }
  };

  const uploadKnowledgeDocument = async () => {
    if (!workflowKnowledgeBase?.id) {
      setError("Create a knowledge base first");
      return;
    }
    if (!kbUploadFile) {
      setError("Choose a document first");
      return;
    }
    setKbBusy(true);
    setError("");
    try {
      const response = await api.fetch("/live-qa/knowledge-base/documents", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          knowledgeBaseId: workflowKnowledgeBase.id,
          title: kbUploadTitle || kbUploadFile.name,
          fileName: kbUploadFile.name,
          mimeType: kbUploadFile.type || "application/pdf",
          base64: await readFileAsBase64(kbUploadFile),
          domainTags: String(kbUploadTags || "")
            .split(",")
            .map((entry) => entry.trim())
            .filter(Boolean),
        }),
      });
      const result = await response.json();
      if (!response.ok || result?.success === false) {
        throw new Error(result?.error || "Failed to upload knowledge document");
      }
      setSuccess("Knowledge document uploaded");
      setKbUploadFile(null);
      setKbUploadTitle("");
      await loadConfig();
    } catch (kbError) {
      setError(kbError.message || "Failed to upload knowledge document");
    } finally {
      setKbBusy(false);
    }
  };

  const requestAssistantPreview = async () => {
    if (!selectedWorkflowId) return;
    setAssistantBusy(true);
    setError("");
    try {
      const response = await api.fetch("/live-qa/config-assistant-preview", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          workflowId: selectedWorkflowId,
          request: assistantInput,
          scopeHints: assistantScope,
          workflowDraft,
          groups: workflowGroups,
        }),
      });
      const result = await response.json();
      if (!response.ok || result?.success === false) {
        throw new Error(result?.error || "Failed to generate configuration preview");
      }
      setAssistantPreview(result);
    } catch (assistantError) {
      setError(assistantError.message || "Failed to generate configuration preview");
    } finally {
      setAssistantBusy(false);
    }
  };

  const applyAssistantPreview = () => {
    if (!assistantPreview) return;
    setWorkflowDraft(assistantPreview.mergedWorkflowPreview || workflowDraft);
    setGroups((current) => applyPreviewToGroups(current, selectedWorkflowId, assistantPreview.mergedGroupsPreview || []));
    setAssistantPreview(null);
    setSuccess("Assistant suggestions applied to the draft. Review them, then save.");
    assistant.onClose();
  };

  const toggleQuickSetupGroup = React.useCallback((groupKey) => {
    setQuickSetupDraft((current) => ({
      ...current,
      groupPresetKeys: current.groupPresetKeys.includes(groupKey)
        ? current.groupPresetKeys.filter((entry) => entry !== groupKey)
        : [...current.groupPresetKeys, groupKey],
    }));
  }, []);

  const runQuickSetup = async () => {
    setQuickSetupBusy(true);
    setError("");
    setSuccess("");

    try {
      const reviewerEmails = parseEmailList(quickSetupDraft.reviewerEmails);
      const operatorEmails = parseEmailList(quickSetupDraft.operatorEmails);
      const institution = String(quickSetupDraft.institution || "").trim();
      const workflowName = String(quickSetupDraft.workflowName || "").trim();

      if (!institution) {
        throw new Error("Institution is required for quick setup");
      }
      if (!workflowName) {
        throw new Error("Workflow name is required for quick setup");
      }
      if (reviewerEmails.length === 0 && operatorEmails.length === 0) {
        throw new Error("Add at least one reviewer or operator email before running quick setup");
      }

      const response = await api.fetch("/live-qa/quick-setup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          institution,
          workflowName,
          reviewerEmails,
          operatorEmails,
          createDemoGroups: quickSetupDraft.createDemoGroups,
          groupPresetKeys: quickSetupDraft.groupPresetKeys,
        }),
      });
      const result = await response.json();
      if (!response.ok || result?.success === false) {
        throw new Error(result?.error || "Failed to run quick setup");
      }

      const invites = [];
      if (quickSetupDraft.sendInvitesToListedUsers) {
        const currentUserEmail = String(user?.email || "").trim().toLowerCase();

        for (const email of reviewerEmails) {
          if (email === currentUserEmail) continue;
          invites.push({
            email,
            institution,
            institutionRole: "instructor",
          });
        }

        for (const email of operatorEmails) {
          if (email === currentUserEmail) continue;
          if (invites.some((entry) => entry.email === email)) continue;
          invites.push({
            email,
            institution,
            institutionRole: "staff",
          });
        }

        for (const invite of invites) {
          try {
            await sendInvite({
              email: invite.email,
              firstName: "",
              lastName: "",
              institution: invite.institution,
              institutionRole: invite.institutionRole,
              accessCodeType: "specific",
              duration: "1_year",
              customMessage: "You have been added to the Live QA pilot workflow.",
              fromSender: "general",
              assistantIds: [],
              agentAccessMode: "none",
              agentNames: [],
            });
          } catch (inviteError) {
            console.warn("Quick setup invite failed", invite.email, inviteError);
          }
        }
      }

      await loadConfig();
      if (result?.workflow?.id) {
        setSelectedWorkflowId(result.workflow.id);
        setWorkflowDraft(result.workflow);
        await loadCharts(result.workflow.id);
      }

      setTabIndex(4);
      quickSetup.onClose();
      setSuccess(
        `Quick setup ready. Workflow "${result?.workflow?.name || workflowName}" is using the shared clinical ground-truth KB.${quickSetupDraft.sendInvitesToListedUsers ? ` Sent ${invites.length} invite${invites.length === 1 ? "" : "s"}.` : ""}`
      );
    } catch (setupError) {
      setError(setupError.message || "Failed to run Live QA quick setup");
    } finally {
      setQuickSetupBusy(false);
    }
  };

  const startTutorial = () => {
    setTutorialStep(0);
    setTabIndex(0);
    tutorial.onOpen();
  };

  const resetTutorial = () => {
    window.localStorage.removeItem(storageKey);
    setTutorialStep(0);
  };

  if (!isClient || !user || loading) {
    return (
      <Flex minH="60vh" align="center" justify="center">
        <Spinner size="xl" />
      </Flex>
    );
  }

  if (!canAccess(user)) {
    return (
      <Box p={8}>
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Access denied</AlertTitle>
          <AlertDescription>Only Gadget administrators and institutional administrators can access Live QA configuration.</AlertDescription>
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={8} maxW="1700px" mx="auto" bg="rgba(15, 23, 42, 0.72)" color="white" minH="calc(100vh - 32px)" borderRadius="2xl" backdropFilter="blur(18px)">
      <Stack spacing={6}>
        <Flex justify="space-between" align={{ base: "flex-start", lg: "center" }} direction={{ base: "column", lg: "row" }} gap={4}>
          <Box ref={registerSectionRef("overview")} data-section="overview">
            <Heading size="lg">Live QA Configurator</Heading>
            <Text color="gray.300">Standalone live chart workflow for clinical, billing, documentation, and compliance QA.</Text>
          </Box>
          <HStack flexWrap="wrap" spacing={3}>
            <Button colorScheme="green" variant="solid" onClick={quickSetup.onOpen}>Quick Setup Tonight</Button>
            <Button colorScheme="blue" leftIcon={<StarIcon />} onClick={startTutorial}>Start Guided Setup</Button>
            <Button as={Link} to="/admin/quick-chart-evaluator" variant="outline">Quick Chart Evaluation</Button>
            <Button as={Link} to="/admin/live-qa-templates" variant="outline">Open Template Builder</Button>
            <Button variant="outline" onClick={reference.onOpen}>Open Reference</Button>
            <Button variant="outline" onClick={assistant.onOpen}>Customize in Plain English</Button>
            <Button as={Link} to="/admin" variant="ghost">Back to Admin</Button>
          </HStack>
        </Flex>

        <Alert status="info" borderRadius="lg">
          <AlertIcon />
          <Box>
            <AlertTitle>Admin-only configuration surface</AlertTitle>
            <AlertDescription>
              Workflow settings, groups, KB documents, and the plain-English config assistant are available only to Gadget admins and institutional admins.
            </AlertDescription>
          </Box>
        </Alert>

        {error ? (
          <Alert status="error" borderRadius="lg">
            <AlertIcon />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}
        {success ? (
          <Alert status="success" borderRadius="lg">
            <AlertIcon />
            <AlertDescription>{success}</AlertDescription>
          </Alert>
        ) : null}

        <Tabs colorScheme="blue" isLazy index={tabIndex} onChange={setTabIndex}>
          <TabList>
            <Tab>Overview</Tab>
            <Tab>Workflow</Tab>
            <Tab>Groups</Tab>
            <Tab>Knowledge Base</Tab>
            <Tab>Test Run</Tab>
            <Tab>Charts</Tab>
          </TabList>

          <TabPanels>
            <TabPanel px={0}>
              <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={5}>
                <Box borderWidth="1px" borderColor="whiteAlpha.200" borderRadius="lg" p={5} bg="rgba(15, 23, 42, 0.78)" boxShadow="xl" backdropFilter="blur(12px)">
                  <Heading size="sm" mb={3}>How Live QA Flows</Heading>
                  <OrderedList spacing={2} pl={4}>
                    <ListItem>Receive chart input through email, upload, structured payload, or pasted text.</ListItem>
                    <ListItem>De-identify the chart before model or KB use.</ListItem>
                    <ListItem>Map content into the canonical structure with audit trails and confidence.</ListItem>
                    <ListItem>Run clinical, billing, documentation, and compliance review.</ListItem>
                    <ListItem>Store de-identified results, chart findings, and field-level mapping audit.</ListItem>
                  </OrderedList>
                </Box>

                <Box borderWidth="1px" borderColor="whiteAlpha.200" borderRadius="lg" p={5} bg="rgba(15, 23, 42, 0.78)" boxShadow="xl" backdropFilter="blur(12px)">
                  <Heading size="sm" mb={3}>What You Usually Change</Heading>
                  <Stack spacing={2}>
                    <Text>Workflow policy JSON for agency rules and thresholds.</Text>
                    <Text>Groups for routing or specialized review buckets.</Text>
                    <Text>Knowledge-base documents for protocols, billing, and SOP guidance.</Text>
                    <Text>Notification recipients, reviewers, and operators.</Text>
                  </Stack>
                </Box>

                <Box borderWidth="1px" borderColor="whiteAlpha.200" borderRadius="lg" p={5} bg="rgba(15, 23, 42, 0.78)" boxShadow="xl" backdropFilter="blur(12px)">
                  <Heading size="sm" mb={3}>Recommended Setup Order</Heading>
                  <OrderedList spacing={2} pl={4}>
                    <ListItem>Select the workflow and confirm institution.</ListItem>
                    <ListItem>Create the knowledge base and upload agency documents.</ListItem>
                    <ListItem>Set workflow policy and required mappings.</ListItem>
                    <ListItem>Create groups and routing rules.</ListItem>
                    <ListItem>Run sample charts and inspect output.</ListItem>
                  </OrderedList>
                </Box>
              </SimpleGrid>
            </TabPanel>

            <TabPanel px={0}>
              <Stack spacing={5}>
                <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={4} ref={registerSectionRef("workflow-header")} data-section="workflow-header">
                  <FormControl>
                    <FormLabel>Workflow</FormLabel>
                    <Select value={selectedWorkflowId} onChange={(event) => handleWorkflowSelection(event.target.value)}>
                      {workflows.map((workflow) => (
                        <option key={workflow.id} value={workflow.id}>{workflow.name}</option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Name</FormLabel>
                    <Input value={workflowDraft.name || ""} onChange={(event) => setWorkflowDraft((current) => ({ ...current, name: event.target.value }))} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Institution</FormLabel>
                    <Input value={workflowDraft.institution || user?.institution || ""} onChange={(event) => setWorkflowDraft((current) => ({ ...current, institution: event.target.value }))} />
                  </FormControl>
                </SimpleGrid>

                <Grid templateColumns={{ base: "1fr", xl: "repeat(2, 1fr)" }} gap={5}>
                  <GridItem ref={registerSectionRef("workflow-intake")}>
                    <JsonEditorCard
                      label="Intake Config"
                      sectionKey="intakeConfig"
                      sectionId="workflow-intake"
                      value={JSON.stringify(workflowDraft.intakeConfig || {}, null, 2)}
                      onChange={(value) => updateJsonField("intakeConfig", value, "{}")}
                      onReset={() => setWorkflowDraft((current) => ({ ...current, intakeConfig: defaultWorkflowDraft.intakeConfig }))}
                    />
                  </GridItem>

                  <GridItem>
                    <JsonEditorCard
                      label="Knowledge Base Config"
                      sectionKey="knowledgeBaseConfig"
                      sectionId="workflow-kb"
                      sectionRef={registerSectionRef("workflow-kb")}
                      value={JSON.stringify(workflowDraft.knowledgeBaseConfig || {}, null, 2)}
                      onChange={(value) => updateJsonField("knowledgeBaseConfig", value, "{}")}
                      onReset={() => setWorkflowDraft((current) => ({ ...current, knowledgeBaseConfig: defaultWorkflowDraft.knowledgeBaseConfig }))}
                    />
                  </GridItem>

                  <GridItem ref={registerSectionRef("workflow-clinical")}>
                    <JsonEditorCard
                      label="Clinical Policy"
                      sectionKey="clinicalPolicyConfig"
                      sectionId="workflow-clinical"
                      value={JSON.stringify(workflowDraft.clinicalPolicyConfig || {}, null, 2)}
                      onChange={(value) => updateJsonField("clinicalPolicyConfig", value, "{}")}
                      onReset={() => setWorkflowDraft((current) => ({ ...current, clinicalPolicyConfig: defaultWorkflowDraft.clinicalPolicyConfig }))}
                    />
                  </GridItem>

                  <GridItem ref={registerSectionRef("workflow-billing")}>
                    <JsonEditorCard
                      label="Billing Policy"
                      sectionKey="billingPolicyConfig"
                      sectionId="workflow-billing"
                      value={JSON.stringify(workflowDraft.billingPolicyConfig || {}, null, 2)}
                      onChange={(value) => updateJsonField("billingPolicyConfig", value, "{}")}
                      onReset={() => setWorkflowDraft((current) => ({ ...current, billingPolicyConfig: defaultWorkflowDraft.billingPolicyConfig }))}
                    />
                  </GridItem>

                  <GridItem ref={registerSectionRef("workflow-compliance")}>
                    <JsonEditorCard
                      label="Compliance Policy"
                      sectionKey="compliancePolicyConfig"
                      sectionId="workflow-compliance"
                      value={JSON.stringify(workflowDraft.compliancePolicyConfig || {}, null, 2)}
                      onChange={(value) => updateJsonField("compliancePolicyConfig", value, "{}")}
                      onReset={() => setWorkflowDraft((current) => ({ ...current, compliancePolicyConfig: defaultWorkflowDraft.compliancePolicyConfig }))}
                    />
                  </GridItem>

                  <GridItem ref={registerSectionRef("workflow-mappings")}>
                    <JsonEditorCard
                      label="Required Mapping Rules"
                      sectionKey="requiredMappingRules"
                      sectionId="workflow-mappings"
                      value={JSON.stringify(workflowDraft.requiredMappingRules || [], null, 2)}
                      onChange={(value) => updateJsonField("requiredMappingRules", value, "[]")}
                      onReset={() => setWorkflowDraft((current) => ({ ...current, requiredMappingRules: defaultWorkflowDraft.requiredMappingRules }))}
                    />
                  </GridItem>

                  <GridItem ref={registerSectionRef("workflow-notifications")}>
                    <JsonEditorCard
                      label="Notification Config"
                      sectionKey="notificationConfig"
                      sectionId="workflow-notifications"
                      value={JSON.stringify(workflowDraft.notificationConfig || {}, null, 2)}
                      onChange={(value) => updateJsonField("notificationConfig", value, "{}")}
                      onReset={() => setWorkflowDraft((current) => ({ ...current, notificationConfig: defaultWorkflowDraft.notificationConfig }))}
                    />
                  </GridItem>

                  <GridItem>
                    <JsonEditorCard
                      label="Report Style Config"
                      sectionKey="reportStyleConfig"
                      sectionId="workflow-report-style"
                      value={JSON.stringify(workflowDraft.reportStyleConfig || {}, null, 2)}
                      onChange={(value) => updateJsonField("reportStyleConfig", value, "{}")}
                      onReset={() => setWorkflowDraft((current) => ({ ...current, reportStyleConfig: defaultWorkflowDraft.reportStyleConfig }))}
                    />
                  </GridItem>
                </Grid>

                <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
                  <Box borderWidth="1px" borderColor="whiteAlpha.200" borderRadius="lg" p={4} bg="rgba(15, 23, 42, 0.78)" boxShadow="xl" backdropFilter="blur(12px)">
                    <Heading size="sm" mb={3}>Reviewer Emails</Heading>
                    <Text fontSize="sm" color="gray.500" mb={3}>These users can review and override field mappings for this workflow.</Text>
                    <Textarea
                      value={Array.isArray(workflowDraft.reviewerEmails) ? workflowDraft.reviewerEmails.join(", ") : workflowDraft.reviewerEmails || ""}
                      onChange={(event) => setWorkflowDraft((current) => ({ ...current, reviewerEmails: event.target.value }))}
                      minH="120px"
                    />
                  </Box>

                  <Box borderWidth="1px" borderColor="whiteAlpha.200" borderRadius="lg" p={4} bg="rgba(15, 23, 42, 0.78)" boxShadow="xl" backdropFilter="blur(12px)">
                    <Heading size="sm" mb={3}>Operator Emails</Heading>
                    <Text fontSize="sm" color="gray.500" mb={3}>These users can access charts assigned to the workflow without configurator access.</Text>
                    <Textarea
                      value={Array.isArray(workflowDraft.operatorEmails) ? workflowDraft.operatorEmails.join(", ") : workflowDraft.operatorEmails || ""}
                      onChange={(event) => setWorkflowDraft((current) => ({ ...current, operatorEmails: event.target.value }))}
                      minH="120px"
                    />
                  </Box>
                </SimpleGrid>

                <HStack>
                  <Button colorScheme="blue" onClick={handleSaveWorkflow} isLoading={saving}>Save Workflow</Button>
                  <Button variant="outline" onClick={() => setWorkflowDraft(workflows.find((entry) => entry.id === selectedWorkflowId) || defaultWorkflowDraft)}>Revert Draft</Button>
                </HStack>
              </Stack>
            </TabPanel>

            <TabPanel px={0}>
              <Stack spacing={5} ref={registerSectionRef("groups-manager")} data-section="groups-manager">
                <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={6}>
                  <Box borderWidth="1px" borderRadius="lg" p={4}>
                    <Heading size="sm" mb={3}>Workflow Groups</Heading>
                    <Text fontSize="sm" color="gray.500" mb={4}>Groups classify charts for routing and targeted review. Match and routing rules stay JSON for v1.</Text>
                    <VStack spacing={3} align="stretch">
                      {workflowGroups.map((group) => (
                        <Box key={group.id} borderWidth="1px" borderRadius="md" p={3}>
                          <Flex justify="space-between" align="flex-start" gap={3}>
                            <Box>
                              <HStack spacing={2}>
                                <Text fontWeight="semibold">{group.name}</Text>
                                <Badge colorScheme={group.active ? "green" : "gray"}>{group.active ? "active" : "archived"}</Badge>
                              </HStack>
                              <Text fontSize="sm" color="gray.500">{group.slug}</Text>
                            </Box>
                            <HStack>
                              <Button size="sm" variant="outline" onClick={() => setGroupDraft({
                                id: group.id,
                                name: group.name,
                                slug: group.slug,
                                active: group.active,
                                matchConfig: group.matchConfig || {},
                                routingConfig: group.routingConfig || {},
                              })}>Edit</Button>
                              <Button size="sm" variant="ghost" colorScheme="red" onClick={() => archiveGroup(group.id)} isLoading={groupSaving}>Archive</Button>
                            </HStack>
                          </Flex>
                          <Code mt={3} whiteSpace="pre-wrap" display="block" maxH="140px" overflow="auto">{JSON.stringify({ matchConfig: group.matchConfig, routingConfig: group.routingConfig }, null, 2)}</Code>
                        </Box>
                      ))}
                      {workflowGroups.length === 0 ? <Text color="gray.500">No groups created yet for this workflow.</Text> : null}
                    </VStack>
                  </Box>

                  <Box borderWidth="1px" borderRadius="lg" p={4}>
                    <Heading size="sm" mb={3}>{groupDraft.id ? "Edit Group" : "Create Group"}</Heading>
                    <Stack spacing={4}>
                      <HStack spacing={2} flexWrap="wrap">
                        {groupPresets.map((preset) => (
                          <Button key={preset.slug} size="sm" variant="outline" onClick={() => applyGroupPreset(preset)}>
                            Use {preset.name}
                          </Button>
                        ))}
                      </HStack>
                      <FormControl>
                        <FormLabel>Name</FormLabel>
                        <Input value={groupDraft.name} onChange={(event) => setGroupDraft((current) => ({ ...current, name: event.target.value, slug: current.slug || buildSlug(event.target.value) }))} />
                      </FormControl>
                      <FormControl>
                        <FormLabel>Slug</FormLabel>
                        <Input value={groupDraft.slug} onChange={(event) => setGroupDraft((current) => ({ ...current, slug: event.target.value }))} />
                      </FormControl>
                      <FormControl>
                        <FormLabel>Match Config JSON</FormLabel>
                        <Textarea
                          value={JSON.stringify(groupDraft.matchConfig || {}, null, 2)}
                          onChange={(event) => {
                            try {
                              const matchConfig = JSON.parse(event.target.value || "{}");
                              setGroupDraft((current) => ({ ...current, matchConfig }));
                              setError("");
                            } catch (_error) {
                              setError("matchConfig is not valid JSON yet");
                            }
                          }}
                          minH="170px"
                          fontFamily="mono"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>Routing Config JSON</FormLabel>
                        <Textarea
                          value={JSON.stringify(groupDraft.routingConfig || {}, null, 2)}
                          onChange={(event) => {
                            try {
                              const routingConfig = JSON.parse(event.target.value || "{}");
                              setGroupDraft((current) => ({ ...current, routingConfig }));
                              setError("");
                            } catch (_error) {
                              setError("routingConfig is not valid JSON yet");
                            }
                          }}
                          minH="170px"
                          fontFamily="mono"
                        />
                      </FormControl>
                      <HStack>
                        <Button colorScheme="blue" onClick={saveGroup} isLoading={groupSaving}>Save Group</Button>
                        <Button variant="outline" onClick={() => setGroupDraft(defaultGroupDraft)}>Reset</Button>
                      </HStack>
                    </Stack>
                  </Box>
                </SimpleGrid>
              </Stack>
            </TabPanel>

            <TabPanel px={0}>
              <Stack spacing={5} ref={registerSectionRef("kb-manager")} data-section="kb-manager">
                <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={6}>
                  <Box borderWidth="1px" borderRadius="lg" p={4}>
                    <Heading size="sm" mb={3}>Knowledge Base</Heading>
                    {workflowKnowledgeBase ? (
                      <Stack spacing={3}>
                        <HStack spacing={3}>
                          <Badge colorScheme="green">{workflowKnowledgeBase.status || "ready"}</Badge>
                          <Text fontWeight="semibold">{workflowKnowledgeBase.name}</Text>
                        </HStack>
                        <Text fontSize="sm" color="gray.500">OpenAI vector store: {workflowKnowledgeBase.openaiVectorStoreId || "pending creation"}</Text>
                        <Code whiteSpace="pre-wrap">{JSON.stringify(workflowDraft.knowledgeBaseConfig || {}, null, 2)}</Code>
                      </Stack>
                    ) : (
                      <Stack spacing={3}>
                        <Text color="gray.500">No workflow KB exists yet. Create one before uploading protocols or policies.</Text>
                        <FormControl>
                          <FormLabel>KB Name</FormLabel>
                          <Input value={newKbName} onChange={(event) => setNewKbName(event.target.value)} placeholder="Agency Live QA KB" />
                        </FormControl>
                        <Button colorScheme="blue" onClick={createKnowledgeBase} isLoading={kbBusy}>Create Knowledge Base</Button>
                      </Stack>
                    )}
                  </Box>

                  <Box borderWidth="1px" borderRadius="lg" p={4}>
                    <Heading size="sm" mb={3}>Upload Knowledge Document</Heading>
                    <Stack spacing={4}>
                      <Text fontSize="sm" color="gray.500">Upload protocols, care bundles, billing policy, narcotic SOPs, or custom QA guidance. Document upload remains manual in v1.</Text>
                      <FormControl>
                        <FormLabel>Title</FormLabel>
                        <Input value={kbUploadTitle} onChange={(event) => setKbUploadTitle(event.target.value)} placeholder="Stroke protocol v2026" />
                      </FormControl>
                      <FormControl>
                        <FormLabel>Domain Tags</FormLabel>
                        <Input value={kbUploadTags} onChange={(event) => setKbUploadTags(event.target.value)} placeholder="clinical_protocol, care_bundle" />
                      </FormControl>
                      <FormControl>
                        <FormLabel>File</FormLabel>
                        <Input type="file" accept=".pdf,.doc,.docx,.txt,.md" onChange={(event) => setKbUploadFile(event.target.files?.[0] || null)} />
                      </FormControl>
                      <Button colorScheme="blue" onClick={uploadKnowledgeDocument} isLoading={kbBusy} isDisabled={!workflowKnowledgeBase}>Upload Document</Button>
                    </Stack>
                  </Box>
                </SimpleGrid>

                <Box borderWidth="1px" borderRadius="lg" p={4}>
                  <Heading size="sm" mb={3}>Knowledge Documents</Heading>
                  <VStack spacing={3} align="stretch">
                    {workflowKnowledgeDocuments.map((document) => (
                      <Flex key={document.id} borderWidth="1px" borderRadius="md" p={3} justify="space-between" align={{ base: "flex-start", md: "center" }} direction={{ base: "column", md: "row" }} gap={3}>
                        <Box>
                          <Text fontWeight="semibold">{document.title}</Text>
                          <Text fontSize="sm" color="gray.500">{(document.domainTags || []).join(", ") || "untagged"}</Text>
                        </Box>
                        <Badge colorScheme={document.syncStatus === "synced" ? "green" : "yellow"}>{document.syncStatus || "pending"}</Badge>
                      </Flex>
                    ))}
                    {workflowKnowledgeDocuments.length === 0 ? <Text color="gray.500">No knowledge documents uploaded yet.</Text> : null}
                  </VStack>
                </Box>
              </Stack>
            </TabPanel>

            <TabPanel px={0}>
              <Stack spacing={5} ref={registerSectionRef("test-run")} data-section="test-run">
                <Box borderWidth="1px" borderRadius="lg" p={4}>
                  <Heading size="sm" mb={3}>Run a Sample Chart Through the Pipeline</Heading>
                  <Text color="gray.500" mb={4}>Use this to validate that the current workflow can process real-world chart inputs and produce de-identified output.</Text>

                  <FormControl maxW="280px" mb={4}>
                    <FormLabel>Input Type</FormLabel>
                    <Select value={testMode} onChange={(event) => setTestMode(event.target.value)}>
                      <option value="artifact">PDF / Image</option>
                      <option value="text">Pasted Text</option>
                      <option value="structured">Structured Payload</option>
                    </Select>
                  </FormControl>

                  {testMode === "artifact" ? (
                    <FormControl>
                      <FormLabel>Upload Sample Chart</FormLabel>
                      <Input type="file" accept=".pdf,image/*,.txt,.json,.xml" onChange={(event) => setTestFile(event.target.files?.[0] || null)} />
                    </FormControl>
                  ) : null}
                  {testMode === "text" ? (
                    <FormControl>
                      <FormLabel>Chart Text</FormLabel>
                      <Textarea value={testText} onChange={(event) => setTestText(event.target.value)} minH="260px" />
                    </FormControl>
                  ) : null}
                  {testMode === "structured" ? (
                    <FormControl>
                      <FormLabel>Structured Payload</FormLabel>
                      <Textarea value={testPayload} onChange={(event) => setTestPayload(event.target.value)} minH="260px" fontFamily="mono" />
                    </FormControl>
                  ) : null}

                  <HStack mt={4}>
                    <Button colorScheme="blue" onClick={handleRunTest} isLoading={running} isDisabled={!selectedWorkflowId}>Run Pipeline</Button>
                    <Text color="gray.500" fontSize="sm">Artifacts are processed server-side, de-identified, and purged after extraction.</Text>
                  </HStack>
                  {!selectedWorkflowId ? (
                    <Text mt={3} color="orange.500" fontSize="sm">
                      {workflows.length === 0
                        ? "No Live QA workflow is available yet. Save a workflow on the Workflow tab first."
                        : "Select a workflow on the Workflow tab before running the pipeline."}
                    </Text>
                  ) : null}
                </Box>
              </Stack>
            </TabPanel>

            <TabPanel px={0}>
              <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={6} ref={registerSectionRef("charts-detail")} data-section="charts-detail">
                <Box borderWidth="1px" borderRadius="lg" p={4}>
                  <Heading size="md" mb={4}>Recent Charts</Heading>
                  <VStack spacing={3} align="stretch">
                    {charts.map((chart) => (
                      <Button
                        key={chart.id}
                        justifyContent="space-between"
                        variant="outline"
                        h="auto"
                        py={3}
                        onClick={() => loadChartDetail(chart.id)}
                      >
                        <Box textAlign="left">
                          <Text fontWeight="semibold">{chart.chartIdentifiers?.incidentNumber || chart.title}</Text>
                          <Text fontSize="sm" color="gray.500">{chart.reviewStatus} · {chart.sourceType}</Text>
                        </Box>
                        <Code>{chart.confidenceSummary ? JSON.stringify(chart.confidenceSummary) : "pending"}</Code>
                      </Button>
                    ))}
                    {charts.length === 0 ? <Text color="gray.500">No charts yet for this workflow.</Text> : null}
                  </VStack>
                </Box>

                <Box borderWidth="1px" borderRadius="lg" p={4}>
                  <Heading size="md" mb={4}>Chart Detail</Heading>
                  {selectedChart?.chart ? (
                    <Stack spacing={4}>
                      <Text fontWeight="semibold">{selectedChart.chart.chartIdentifiers?.incidentNumber || selectedChart.chart.title}</Text>
                      <Box>
                        <Text fontSize="sm" color="gray.500" mb={2}>Extraction Result</Text>
                        <Code whiteSpace="pre-wrap" display="block" maxH="180px" overflow="auto">{JSON.stringify(selectedChart.chart.extractionResult, null, 2)}</Code>
                      </Box>
                      <Box>
                        <Text fontSize="sm" color="gray.500" mb={2}>Synthesized Report</Text>
                        <Code whiteSpace="pre-wrap" display="block" maxH="220px" overflow="auto">{JSON.stringify(selectedChart.chart.synthesizedReport, null, 2)}</Code>
                      </Box>
                      <Box>
                        <Text fontSize="sm" color="gray.500" mb={2}>Field Audit</Text>
                        <Code whiteSpace="pre-wrap" display="block" maxH="220px" overflow="auto">{JSON.stringify(selectedChart.fields || [], null, 2)}</Code>
                      </Box>
                    </Stack>
                  ) : (
                    <Text color="gray.500">Select a processed chart to inspect the de-identified output and mapping audit.</Text>
                  )}
                </Box>
              </SimpleGrid>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Stack>

      <Drawer isOpen={tutorial.isOpen} placement="right" onClose={tutorial.onClose} size="md">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Live QA Guided Setup</DrawerHeader>
          <DrawerBody>
            <Stack spacing={5}>
              <Badge alignSelf="flex-start">Step {tutorialStep + 1} of {tutorialSteps.length}</Badge>
              <Box>
                <Heading size="md" mb={2}>{tutorialSteps[tutorialStep].title}</Heading>
                <Text color="gray.600">{tutorialSteps[tutorialStep].body}</Text>
              </Box>
              <Divider />
              <Box>
                <Text fontWeight="semibold" mb={2}>Jump to any step</Text>
                <VStack align="stretch" spacing={2}>
                  {tutorialSteps.map((step, index) => (
                    <Button key={step.title} variant={index === tutorialStep ? "solid" : "ghost"} justifyContent="flex-start" onClick={() => setTutorialStep(index)}>
                      {index + 1}. {step.title}
                    </Button>
                  ))}
                </VStack>
              </Box>
              <Divider />
              <HStack justify="space-between">
                <Button variant="outline" onClick={() => setTutorialStep((current) => Math.max(0, current - 1))} isDisabled={tutorialStep === 0}>Back</Button>
                <HStack>
                  <Button variant="ghost" onClick={resetTutorial}>Reset</Button>
                  <Button colorScheme="blue" onClick={() => {
                    if (tutorialStep >= tutorialSteps.length - 1) {
                      tutorial.onClose();
                    } else {
                      setTutorialStep((current) => current + 1);
                    }
                  }}>
                    {tutorialStep >= tutorialSteps.length - 1 ? "Done" : "Next"}
                  </Button>
                </HStack>
              </HStack>
            </Stack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      <Drawer isOpen={reference.isOpen} placement="right" onClose={reference.onClose} size="lg">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Live QA Reference</DrawerHeader>
          <DrawerBody>
            <Accordion allowMultiple>
              {Object.entries(helpContent).map(([key, content]) => (
                <AccordionItem key={key}>
                  <AccordionButton>
                    <Box flex="1" textAlign="left">{content.title}</Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4}>
                    <Stack spacing={3}>
                      <Text>{content.description}</Text>
                      <Text color="gray.600"><strong>When to change it:</strong> {content.when}</Text>
                      <Text color="gray.600"><strong>Common mistake:</strong> {content.mistakes}</Text>
                      <Code whiteSpace="pre-wrap">{content.example}</Code>
                    </Stack>
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      <Modal isOpen={quickSetup.isOpen} onClose={quickSetup.onClose} size="3xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Quick Setup Tonight</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={5}>
              <Alert status="info">
                <AlertIcon />
                <AlertDescription>
                  This creates a demo-ready Live QA workflow with the shared clinical ground-truth vector store, softer proof-of-concept billing/compliance defaults, and optional routing groups. Use this to get charts reading tonight, then refine the details later.
                </AlertDescription>
              </Alert>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Institution</FormLabel>
                  <Input
                    value={quickSetupDraft.institution}
                    onChange={(event) => setQuickSetupDraft((current) => ({ ...current, institution: event.target.value }))}
                    placeholder="Demo institution"
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Workflow Name</FormLabel>
                  <Input
                    value={quickSetupDraft.workflowName}
                    onChange={(event) => setQuickSetupDraft((current) => ({ ...current, workflowName: event.target.value }))}
                    placeholder="Live QA Pilot"
                  />
                </FormControl>
              </SimpleGrid>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel>Reviewer Emails</FormLabel>
                  <Textarea
                    value={quickSetupDraft.reviewerEmails}
                    onChange={(event) => setQuickSetupDraft((current) => ({ ...current, reviewerEmails: event.target.value }))}
                    minH="120px"
                    placeholder="you@example.org, medical.director@example.org"
                  />
                  <Text mt={2} fontSize="sm" color="gray.500">
                    These users get detailed chart-review access through workflow assignment.
                  </Text>
                </FormControl>
                <FormControl>
                  <FormLabel>Operator Emails</FormLabel>
                  <Textarea
                    value={quickSetupDraft.operatorEmails}
                    onChange={(event) => setQuickSetupDraft((current) => ({ ...current, operatorEmails: event.target.value }))}
                    minH="120px"
                    placeholder="ops@example.org"
                  />
                  <Text mt={2} fontSize="sm" color="gray.500">
                    Use this for operational or oversight recipients who should work the queue.
                  </Text>
                </FormControl>
              </SimpleGrid>

              <Box borderWidth="1px" borderRadius="lg" p={4}>
                <Stack spacing={3}>
                  <Checkbox
                    isChecked={quickSetupDraft.createDemoGroups}
                    onChange={(event) => setQuickSetupDraft((current) => ({ ...current, createDemoGroups: event.target.checked }))}
                  >
                    Create demo routing groups
                  </Checkbox>
                  {quickSetupDraft.createDemoGroups ? (
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                      {quickSetupGroupOptions.map((option) => (
                        <Checkbox
                          key={option.key}
                          isChecked={quickSetupDraft.groupPresetKeys.includes(option.key)}
                          onChange={() => toggleQuickSetupGroup(option.key)}
                        >
                          {option.label}
                        </Checkbox>
                      ))}
                    </SimpleGrid>
                  ) : null}
                </Stack>
              </Box>

              <Checkbox
                isChecked={quickSetupDraft.sendInvitesToListedUsers}
                onChange={(event) => setQuickSetupDraft((current) => ({ ...current, sendInvitesToListedUsers: event.target.checked }))}
              >
                Send invites to listed emails after setup
              </Checkbox>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <HStack>
              <Button variant="ghost" onClick={quickSetup.onClose}>Close</Button>
              <Button colorScheme="green" onClick={runQuickSetup} isLoading={quickSetupBusy}>
                Build Demo Workflow
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={assistant.isOpen} onClose={assistant.onClose} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Customize Live QA in Plain English</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={5}>
              <Alert status="info">
                <AlertIcon />
                <AlertDescription>
                  This assistant only suggests structured diffs. It does not save directly. Review the preview, apply it to the draft, then save the workflow or groups explicitly.
                </AlertDescription>
              </Alert>

              <Box>
                <Text fontWeight="semibold" mb={2}>Example requests</Text>
                <VStack align="stretch" spacing={2}>
                  {assistantExamples.map((example) => (
                    <Button key={example} variant="ghost" justifyContent="flex-start" onClick={() => setAssistantInput(example)}>
                      {example}
                    </Button>
                  ))}
                </VStack>
              </Box>

              <FormControl>
                <FormLabel>Request</FormLabel>
                <Textarea value={assistantInput} onChange={(event) => setAssistantInput(event.target.value)} minH="140px" />
              </FormControl>

              <FormControl>
                <FormLabel>Scope</FormLabel>
                <Select
                  value={assistantScope[0] || "workflow"}
                  onChange={(event) => setAssistantScope([event.target.value])}
                  maxW="260px"
                >
                  <option value="workflow">Workflow</option>
                  <option value="clinical">Clinical</option>
                  <option value="billing">Billing</option>
                  <option value="compliance">Compliance</option>
                  <option value="mappings">Mappings</option>
                  <option value="notifications">Notifications</option>
                  <option value="groups">Groups</option>
                </Select>
              </FormControl>

              <Button colorScheme="blue" alignSelf="flex-start" onClick={requestAssistantPreview} isLoading={assistantBusy}>
                Generate Preview
              </Button>

              {assistantPreview ? (
                <Stack spacing={4}>
                  <Box borderWidth="1px" borderRadius="lg" p={4}>
                    <Heading size="sm" mb={2}>Summary</Heading>
                    <Text>{assistantPreview.summary}</Text>
                  </Box>

                  {(assistantPreview.assumptions || []).length ? (
                    <Box borderWidth="1px" borderRadius="lg" p={4}>
                      <Heading size="sm" mb={2}>Assumptions</Heading>
                      <OrderedList pl={4}>
                        {assistantPreview.assumptions.map((entry) => <ListItem key={entry}>{entry}</ListItem>)}
                      </OrderedList>
                    </Box>
                  ) : null}

                  {(assistantPreview.warnings || assistantPreview.unsupportedRequests || []).length ? (
                    <Box borderWidth="1px" borderRadius="lg" p={4}>
                      <Heading size="sm" mb={2}>Warnings</Heading>
                      <VStack align="stretch" spacing={2}>
                        {(assistantPreview.warnings || []).map((entry) => <Text key={entry}>{entry}</Text>)}
                        {(assistantPreview.unsupportedRequests || []).map((entry) => <Text key={entry} color="orange.500">{entry}</Text>)}
                      </VStack>
                    </Box>
                  ) : null}

                  <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4}>
                    <Box borderWidth="1px" borderRadius="lg" p={4}>
                      <Heading size="sm" mb={2}>Workflow Patch</Heading>
                      <Code whiteSpace="pre-wrap" display="block" maxH="280px" overflow="auto">{JSON.stringify(assistantPreview.workflowPatch || [], null, 2)}</Code>
                    </Box>
                    <Box borderWidth="1px" borderRadius="lg" p={4}>
                      <Heading size="sm" mb={2}>Group Operations</Heading>
                      <Code whiteSpace="pre-wrap" display="block" maxH="280px" overflow="auto">{JSON.stringify(assistantPreview.groupOperations || [], null, 2)}</Code>
                    </Box>
                    <Box borderWidth="1px" borderRadius="lg" p={4}>
                      <Heading size="sm" mb={2}>Merged Workflow Preview</Heading>
                      <Code whiteSpace="pre-wrap" display="block" maxH="280px" overflow="auto">{JSON.stringify(assistantPreview.mergedWorkflowPreview || {}, null, 2)}</Code>
                    </Box>
                    <Box borderWidth="1px" borderRadius="lg" p={4}>
                      <Heading size="sm" mb={2}>Merged Groups Preview</Heading>
                      <Code whiteSpace="pre-wrap" display="block" maxH="280px" overflow="auto">{JSON.stringify(assistantPreview.mergedGroupsPreview || [], null, 2)}</Code>
                    </Box>
                  </SimpleGrid>
                </Stack>
              ) : null}
            </Stack>
          </ModalBody>
          <ModalFooter>
            <HStack>
              <Button variant="ghost" onClick={assistant.onClose}>Close</Button>
              <Button colorScheme="blue" onClick={applyAssistantPreview} isDisabled={!assistantPreview}>Apply to Draft</Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
